"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import JourneyCard from "./JourneyCard";
import { useTranslation } from "react-i18next";
import { useLocaleRouter } from "@/hooks/useLocaleRouter";
import { trpc } from "@/lib/trpc/client";
import { Text } from "@/components/ui/Headings";

const SignatureJourneysSection: React.FC = () => {
  const { t } = useTranslation();
  const { createLocalizedPath } = useLocaleRouter();

  // Fetch journeys from database
  const { data: rawJourneys = [], isLoading } =
    trpc.signatureJourneys.getAll.useQuery();

  // Type-safe transformation of raw journey data with multilingual support
  const journeys = rawJourneys.map((journey) => ({
    ...journey,
    title: journey.title as { en: string; de: string; uk: string },
    subtitle: journey.subtitle as { en: string; de: string; uk: string },
    description: journey.description as { en: string; de: string; uk: string },
    country: journey.country as { en: string; de: string; uk: string } | null,
    currency: journey.currency || "EUR", // Default to EUR if null
    highlights: journey.highlights as {
      en: string[];
      de: string[];
      uk: string[];
    },
    overview: journey.overview as { en: string; de: string; uk: string } | null,
    whatMakesItSpecial: journey.whatMakesItSpecial as {
      en: string;
      de: string;
      uk: string;
    } | null,
    itinerary: journey.itinerary as {
      en: Array<{ day: number; title: string; description: string }>;
      de: Array<{ day: number; title: string; description: string }>;
      uk: Array<{ day: number; title: string; description: string }>;
    } | null,
    includes: journey.includes as { en: string[]; de: string[]; uk: string[] },
    notIncluded: journey.notIncluded as {
      en: string[];
      de: string[];
      uk: string[];
    },
    availableDates: journey.availableDates as Array<{
      month: string;
      status: string;
      price: string;
    }> | null,
    guaranteedDepartures: journey.guaranteedDepartures as Array<{
      date: string;
      status: string;
      price: string;
      currency: string;
    }> | null,
  }));

  if (isLoading) {
    return (
      <section className="pt-6 pb-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-base text-black">
              {t("home.signatureSection.loading")}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (journeys.length === 0) {
    return null; // Don't show section if no journeys
  }

  return (
    <section
      className="
    pt-4 sm:pt-6 
    pb-4 sm:pb-6 
    bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-4 sm:mb-6 lg:mb-6 hidden md:block">
          <Text
            variant="subtitle"
            className="text-[#232B2B] max-w-4xl mx-auto px-4 leading-relaxed"
          >
            {t("home.signatureSection.intro1")}
          </Text>
          <Text
            variant="subtitle"
            className="text-[#232B2B] max-w-4xl mx-auto px-4 leading-relaxed"
          >
            {t("home.signatureSection.intro2")}
          </Text>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8 items-stretch">
          {journeys.slice(0, 4).map((journey) => (
            <JourneyCard key={journey.id} journey={journey} />
          ))}
        </div>
        <div className="text-center mt-4 sm:mt-6 lg:mt-8">
          <Link
            href={createLocalizedPath("/signature-journeys")}
            className="inline-flex items-center text-white px-6 lg:px-8 py-2.5 lg:py-3 rounded-full font-bold transition-colors bg-primary-600 hover:bg-primary-550 touch-target text-xs lg:text-base"
          >
            {t("home.signatureSection.cta")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SignatureJourneysSection;
